import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  FiPlay, FiSend, FiMessageCircle, FiZap, FiRefreshCw,
  FiGlobe, FiCheck, FiClock, FiExternalLink,
} from "react-icons/fi";

const DEMO_QA = [
  { q: "What is NexaGPT?", a: "NexaGPT is an AI chatbot builder that lets you train on your website, PDFs, Notion pages, YouTube videos, and more. No code required — just connect your sources and embed the widget." },
  { q: "How do I train on a PDF?", a: "Go to Content → Files, click 'Upload Files', and select your PDF. NexaGPT will automatically extract and index the content. Your chatbot will be able to answer questions from it within minutes." },
  { q: "What pricing plans are available?", a: "We offer three plans: Starter at $29/month (2 chatbots, 10k messages), Pro at $79/month (10 chatbots, 100k messages, CRM integrations), and Enterprise with custom pricing for unlimited scale." },
  { q: "Can I embed this on my website?", a: "Yes! After creating your chatbot, go to the Dashboard and copy the embed script. Paste it just before the </body> tag on your website. It works on any platform — Webflow, WordPress, Shopify, or custom HTML." },
  { q: "Does it support multiple languages?", a: "NexaGPT supports 15+ languages including English, Hindi, Spanish, French, German, Arabic, and Japanese. You can set the default language in Settings → Localization or enable auto-detection." },
  { q: "How do I capture leads?", a: "Enable lead capture in Settings → User Data. You can collect name, email, and phone number — either as mandatory fields before the chat starts, or as optional prompts during the conversation." },
];

const SUGGESTIONS = [
  "What is NexaGPT?",
  "How do I train on a PDF?",
  "What pricing plans are available?",
  "Can I embed this on my website?",
];

const FEATURES = [
  { icon: FiZap,           label: "Responds instantly with AI",          desc: "Sub-second response times powered by GPT-4" },
  { icon: FiGlobe,         label: "Trained on real NexaGPT content",     desc: "Accurate answers from your own knowledge base" },
  { icon: FiMessageCircle, label: "Same experience your visitors get",   desc: "Pixel-perfect preview of your live widget" },
  { icon: FiCheck,         label: "Lead capture & conversation history", desc: "Every chat stored and searchable in Inbox" },
];

function LiveChatDemo({ chatbotName }) {
  const name = chatbotName || "NexaGPT Assistant";
  const [messages, setMessages] = useState([
    { role: "bot", text: `Hi! I'm ${name}. Ask me anything about NexaGPT — pricing, features, setup, or integrations.` },
  ]);
  const [input, setInput]   = useState("");
  const [typing, setTyping] = useState(false);
  const msgsRef = useRef(null);

  useEffect(() => {
    if (!msgsRef.current) return;
    msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  }, [messages, typing]);

  const getAnswer = (question) => {
    const q = question.toLowerCase();
    const match = DEMO_QA.find((item) => {
      const keywords = item.q.toLowerCase().split(" ").filter((w) => w.length > 3);
      return keywords.some((kw) => q.includes(kw));
    });
    return match?.a || "Great question! NexaGPT makes it easy to deploy AI chatbots trained on your own content. Connect websites, PDFs, Notion, YouTube, and more — no code needed. Start your free trial at nexagpt.ai.";
  };

  const send = (text) => {
    const msg = text || input.trim();
    if (!msg) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: msg }]);
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages((prev) => [...prev, { role: "bot", text: getAnswer(msg) }]);
    }, 900 + Math.random() * 500);
  };

  const reset = () => {
    setMessages([{ role: "bot", text: `Hi! I'm ${name}. Ask me anything about NexaGPT — pricing, features, setup, or integrations.` }]);
    setInput("");
    setTyping(false);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-md overflow-hidden flex flex-col" style={{ height: 540 }}>

      {/* Chat header */}
      <div className="bg-gradient-to-r from-primary to-indigo-500 px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white font-bold text-sm shadow-sm ring-2 ring-white/30">
            N
          </div>
          <div>
            <p className="text-sm font-semibold text-white leading-tight">{name}</p>
            <p className="text-[11px] text-white/70 flex items-center gap-1.5 mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block animate-pulse" />
              Online · Typically replies instantly
            </p>
          </div>
        </div>
        <button
          onClick={reset}
          title="Reset chat"
          className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/80 hover:text-white transition"
        >
          <FiRefreshCw size={13} />
        </button>
      </div>

      {/* Messages */}
      <div ref={msgsRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gray-50/70">
        {messages.map((m, i) => (
          <div key={i} className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "bot" && (
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-indigo-500 text-white flex items-center justify-center text-xs font-bold shrink-0 shadow-sm shadow-primary/20">
                N
              </div>
            )}
            <div className={`max-w-[78%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm ${
              m.role === "user"
                ? "bg-primary text-white rounded-br-sm shadow-primary/20"
                : "bg-white border border-gray-100 text-gray-800 rounded-bl-sm"
            }`}>
              {m.text}
            </div>
          </div>
        ))}

        {typing && (
          <div className="flex items-end gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-indigo-500 text-white flex items-center justify-center text-xs font-bold shrink-0 shadow-sm shadow-primary/20">
              N
            </div>
            <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
              <div className="flex gap-1 items-center h-3">
                <span className="w-2 h-2 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Suggestions */}
      {messages.length <= 1 && (
        <div className="px-4 pb-3 pt-1 flex flex-wrap gap-1.5 bg-gray-50/70 border-t border-gray-100">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              onClick={() => send(s)}
              className="text-[11px] px-3 py-1.5 border border-primary/25 text-primary bg-primary-light/60 rounded-full hover:bg-primary/10 hover:border-primary/50 transition font-medium"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-t bg-white shrink-0">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
          placeholder="Type a message…"
          className="flex-1 text-sm px-3.5 py-2 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-gray-50 transition"
        />
        <button
          onClick={() => send()}
          disabled={!input.trim() || typing}
          className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center hover:opacity-90 disabled:opacity-40 transition shadow-sm shadow-primary/30"
        >
          <FiSend size={13} />
        </button>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-center gap-1.5 py-2 bg-white border-t border-gray-100">
        <div className="w-3.5 h-3.5 rounded bg-gradient-to-br from-primary to-indigo-400 flex items-center justify-center">
          <span className="text-white text-[7px] font-bold">N</span>
        </div>
        <p className="text-[11px] text-gray-400">
          Powered by <span className="font-semibold text-primary">NexaGPT</span>
        </p>
      </div>
    </div>
  );
}

export default function Demo() {
  const { selected } = useSelector((s) => s.chatbots);

  const videos = [
    {
      id: 1,
      title: "Chatbot Setup Walkthrough",
      description: "Learn how to create and configure your chatbot in minutes.",
      thumbnail: "https://img.youtube.com/vi/dQw4w9WgXcQ/hqdefault.jpg",
      videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
      duration: "3:42",
    },
    {
      id: 2,
      title: "Website Training Demo",
      description: "How to train your chatbot with website URLs and crawl entire domains.",
      thumbnail: "https://img.youtube.com/vi/9bZkp7q19f0/hqdefault.jpg",
      videoUrl: "https://www.youtube.com/watch?v=9bZkp7q19f0",
      duration: "4:15",
    },
    {
      id: 3,
      title: "Dashboard & Analytics Overview",
      description: "Explore analytics, conversation history, monitoring, and settings.",
      thumbnail: "https://img.youtube.com/vi/l482T0yNkeo/hqdefault.jpg",
      videoUrl: "https://www.youtube.com/watch?v=l482T0yNkeo",
      duration: "5:30",
    },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-10 px-0 py-2">

      {/* ── Page header ── */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2.5 mb-2">
            <span className="flex items-center gap-1.5 text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              Live Demo
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Try it yourself</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-lg">
            Interact with a live NexaGPT chatbot and explore product walkthroughs — no setup required.
          </p>
        </div>
      </div>

      {/* ── Live demo: description + chat widget ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">

        {/* Left — description */}
        <div className="space-y-6">

          {/* Hero text */}
          <div>
            <h2 className="text-3xl font-bold text-gray-900 leading-tight mb-3">
              Your chatbot,{" "}
              <span className="bg-gradient-to-r from-primary to-indigo-500 bg-clip-text text-transparent">
                live right here
              </span>
            </h2>
            <p className="text-sm text-gray-500 leading-relaxed">
              This is an interactive preview of your NexaGPT chatbot. Ask it anything — pricing, features,
              integrations, or how to get started. This is exactly what your website visitors will experience.
            </p>
          </div>

          {/* Feature cards */}
          <div className="space-y-3">
            {FEATURES.map(({ icon: Icon, label, desc }) => (
              <div
                key={label}
                className="flex items-center gap-3.5 p-3.5 bg-white border border-gray-100 rounded-xl shadow-sm hover:border-primary/20 hover:shadow transition-all"
              >
                <div className="w-9 h-9 rounded-xl bg-primary-light flex items-center justify-center shrink-0">
                  <Icon size={15} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800 leading-tight">{label}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Tips card */}
          <div className="bg-gradient-to-br from-primary/5 to-indigo-50 border border-primary/10 rounded-xl p-4">
            <p className="text-xs font-semibold text-primary mb-2 flex items-center gap-1.5">
              <FiZap size={12} /> Pro tip
            </p>
            <p className="text-xs text-gray-600 leading-relaxed">
              Use the suggestion chips to quickly test common questions, or type your own. Click the reset
              button in the chat header to start a fresh conversation anytime.
            </p>
          </div>

        </div>

        {/* Right — live chatbot widget */}
        <div className="lg:sticky lg:top-4">
          <LiveChatDemo chatbotName={selected?.chatbot_name} />
        </div>
      </div>

      {/* ── Divider ── */}
      <div className="border-t border-gray-100" />

      {/* ── Video Walkthroughs ── */}
      <div>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Video Walkthroughs</h2>
            <p className="text-sm text-gray-500 mt-0.5">Step-by-step guides to get the most out of NexaGPT.</p>
          </div>
          <span className="text-xs font-semibold bg-gray-100 text-gray-500 px-2.5 py-1 rounded-full">
            {videos.length} videos
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {videos.map((video) => (
            <div
              key={video.id}
              onClick={() => window.open(video.videoUrl, "_blank")}
              className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md hover:border-primary/20 transition-all cursor-pointer group"
            >
              {/* Thumbnail */}
              <div className="relative overflow-hidden bg-gray-100">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {/* Play overlay */}
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center shadow-lg shadow-primary/40 scale-90 group-hover:scale-100 transition-transform">
                    <FiPlay className="text-white ml-0.5" size={18} />
                  </div>
                </div>
                {/* Duration pill */}
                <div className="absolute bottom-2.5 left-2.5 flex items-center gap-1 bg-black/70 text-white text-[11px] font-medium px-2 py-0.5 rounded-full backdrop-blur-sm">
                  <FiClock size={9} />
                  {video.duration}
                </div>
              </div>

              {/* Card body */}
              <div className="px-4 py-3.5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-sm font-semibold text-gray-900 leading-snug group-hover:text-primary transition-colors">
                    {video.title}
                  </h3>
                  <FiExternalLink size={12} className="text-gray-300 group-hover:text-primary transition-colors shrink-0 mt-0.5" />
                </div>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed line-clamp-2">{video.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
